import { useParams, Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useLocation } from "@/contexts/LocationContext";
import { Star, Minus, Plus, ShoppingCart, MapPin, Clock, ChevronRight, Truck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import ProductCard from "@/components/products/ProductCard";
import { useStorefrontProductById, useStorefrontStoreById, useStoreProducts } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";

const demoReviews = [
  { id: 1, name: "Rahul G.", rating: 5, date: "2 days ago", comment: "Very fresh and great quality. Will order again!" },
  { id: 2, name: "Meera K.", rating: 4, date: "5 days ago", comment: "Good product, delivered on time. Slightly smaller quantity than expected." },
  { id: 3, name: "Deepak Y.", rating: 5, date: "1 week ago", comment: "Excellent! Best in the market. Highly recommended." },
];

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { addItem, items, updateQuantity } = useCart();
  const { nearbyProducts } = useLocation();
  const { data: product, isLoading } = useStorefrontProductById(productId, Number.isFinite(productId));
  const storeId = product ? Number(product.storeId) : 0;
  const { data: store } = useStorefrontStoreById(storeId, storeId > 0);
  const { data: storeProductsData } = useStoreProducts(storeId, 1, 20, storeId > 0);
  const [selectedImage, setSelectedImage] = useState(0);

  const storeProducts = useMemo(
    () => (storeProductsData?.items ?? []).filter((p) => p.id !== product?.id).slice(0, 4),
    [storeProductsData, product?.id],
  );

  const relatedProducts = useMemo(
    () =>
      product
        ? nearbyProducts.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4)
        : [],
    [nearbyProducts, product],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background container mx-auto px-4 py-8">
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="font-heading text-xl font-bold mb-2">Product not found</p>
          <Link to="/products"><Button variant="outline">Back to Products</Button></Link>
        </div>
      </div>
    );
  }

  const cartItem = items.find((i) => i.product.id === product.id);
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  // Demo: multiple images from same source with different crops
  const images = [
    product.image,
    product.image.replace("fit=crop", "fit=crop&sat=-100"),
    product.image.replace("fit=crop", "fit=crop&flip=h"),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-3">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/products" className="hover:text-foreground transition-colors">Products</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-foreground font-medium truncate">{product.name}</span>
        </nav>
      </div>

      <div className="container mx-auto px-4 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12"
        >
          {/* Image Gallery */}
          <div className="space-y-3">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-border/50">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-contain"
              />
              {discount > 0 && (
                <Badge className="absolute top-4 left-4 bg-accent text-accent-foreground text-sm px-3 py-1">
                  {discount}% OFF
                </Badge>
              )}
              {product.tags?.includes("Bestseller") && (
                <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">Bestseller</Badge>
              )}
            </div>
            <div className="flex gap-2">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 bg-white transition-all ${selectedImage === i ? "border-primary ring-2 ring-primary/20" : "border-border/50 opacity-70 hover:opacity-100"
                    }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-5">
            <div>
              <Link
                to={`/stores/${product.storeId}`}
                className="text-sm text-primary font-medium hover:underline"
              >
                {product.storeName}
              </Link>
              <h1 className="font-heading text-2xl md:text-3xl font-bold mt-1">{product.name}</h1>
              <p className="text-muted-foreground mt-1">{product.unit}</p>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-success/10">
                <Star className="w-4 h-4 fill-warning text-warning" />
                <span className="font-semibold text-sm">{product.rating}</span>
              </div>
              <span className="text-sm text-muted-foreground">{product.reviewCount} ratings</span>
            </div>

            {/* Price */}
            {(() => {
              const bv = product.businessVolume && product.businessVolume > 0
                ? product.businessVolume
                : Math.max(1, Math.round(product.price * 0.1));
              return (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading text-3xl font-bold text-primary">₹{product.price}</span>
                  {product.originalPrice && (
                    <>
                      <span className="text-lg text-muted-foreground line-through">₹{product.originalPrice}</span>
                      <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">Save ₹{product.originalPrice - product.price}</Badge>
                    </>
                  )}
                  <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs font-bold px-2.5 py-1">
                    ⚡ {bv} BV Rewards
                  </Badge>
                </div>
              );
            })()}

            <Separator />

            {/* Description */}
            <div>
              <h3 className="font-heading font-semibold mb-2">About this product</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{product.description}</p>
              {product.tags && product.tags.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {product.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Add to Cart */}
            <div className="flex items-center gap-3">
              {cartItem ? (
                <div className="flex items-center gap-3 bg-secondary rounded-xl px-2 py-1.5 border border-border">
                  <button
                    onClick={() => updateQuantity(product.id, cartItem.quantity - 1)}
                    className="w-9 h-9 rounded-lg bg-card flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-heading font-bold text-lg w-8 text-center">{cartItem.quantity}</span>
                  <button
                    onClick={() => updateQuantity(product.id, cartItem.quantity + 1)}
                    className="w-9 h-9 rounded-lg bg-card flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => addItem(product)}
                  className="h-12 px-8 bg-gradient-primary hover:opacity-90 text-primary-foreground font-heading font-semibold rounded-xl text-base"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" /> Add to Cart
                </Button>
              )}
            </div>

            <Separator />

            {/* Store Info */}
            {store && (
              <Link
                to={`/stores/${store.id}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-secondary/50 border border-border/50 hover:bg-secondary transition-colors"
              >
                <img
                  src={store.image}
                  alt={store.name}
                  className="w-14 h-14 rounded-xl object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm">{store.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{store.distance}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{store.deliveryTime}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
            )}

            {/* Guarantees */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-secondary/50 border border-border/50">
                <Truck className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs font-semibold">Fast Delivery</p>
                  <p className="text-xs text-muted-foreground">{store?.deliveryTime || "15-30 min"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-secondary/50 border border-border/50">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs font-semibold">Quality Assured</p>
                  <p className="text-xs text-muted-foreground">Freshness guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reviews */}
        <div className="mb-12">
          <h2 className="font-heading text-xl font-bold mb-5">Customer Reviews</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {demoReviews.map((review) => (
              <div key={review.id} className="p-4 rounded-xl bg-card border border-border/50 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm">{review.name}</p>
                  <span className="text-xs text-muted-foreground">{review.date}</span>
                </div>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? "fill-warning text-warning" : "text-muted-foreground/30"}`} />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>

        {/* More from this store */}
        {storeProducts.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold">More from {product.storeName}</h2>
              <Link to={`/stores/${product.storeId}`} className="text-sm font-semibold text-primary flex items-center gap-1 hover:gap-2 transition-all">
                View Store <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {storeProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* Related products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="font-heading text-xl font-bold mb-5">Similar Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
